/**
 * Tiny feature-flag mechanism.
 *
 * No BE roundtrip, no env-var dance. Two layers, in priority order:
 *
 *   1. URL search param `?ff.<key>=1` (set) or `?ff.<key>=0`
 *      (force-off). In production the URL never reaches this file
 *      because `captureFeatureFlagsFromUrl()` in `bootstrap.ts`
 *      runs at app startup — BEFORE TanStack Router mounts and
 *      strips unknown params — and persists every `ff.*` param to
 *      localStorage. So `readUrlOverride` below is the test-only
 *      escape hatch: it lets `isFeatureEnabled` be called directly
 *      (without going through main.tsx bootstrap) and still see a
 *      URL override that hasn't been captured yet.
 *   2. localStorage key `ff.<key>` (`"1"` / `"0"`) — the primary
 *      lookup in production. Populated either by the bootstrap or
 *      by the user/devtool directly.
 *   3. Compile-time `DEFAULTS` map below.
 *
 * Setting `?ff.<key>=0` lets a user disable a flag even when the
 * default is on. After one visit the choice persists in their
 * browser until they reset it.
 *
 * Flags here are short-lived rollout switches. Once a flag's default
 * is `true` for ≥ 1 release and there are no reported regressions,
 * delete it and the surrounding gate — the codebase is the source of
 * truth, not the flag.
 */

export const FEATURE_FLAGS = {
  /**
   * Phase B: route MyApp table metadata + permissions + tableStates
   * through TanStack Query so revisits are cache-served (5-min
   * staleTime). Imperative useEffect/useCallback fetchers are kept as
   * the off-branch so we can flip back instantly if a regression
   * surfaces.
   */
  "myapp.cache.tanstack": false,
  /**
   * Reporting Phase 2.1: swap the Custom Report builder's Pivot / Matrix
   * accordion from the legacy pivotColumn / pivotGroupColumns /
   * pivotMeasures editor (StepColumns) for the canonical Rows / Columns /
   * Values three-dropzone editor (RcvDropzoneEditor). When on, the save
   * path PATCHes only the RCV fields; the data-plane write-through
   * translator in `report_repository._translate_new_to_old_shape`
   * re-derives the legacy pivot* fields so the executor renders
   * unchanged.
   *
   * Default ON for all users. The flag is retained purely as an
   * emergency-rollback escape hatch: a user who hits a regression can
   * fall back to the legacy editor in their own browser via
   * `?ff.reporting.rcv.dropzoneEditor=0` (persisted to localStorage on
   * first visit). The first-mount canonicalisation in
   * `RcvDropzoneEditor` derives RCV from legacy on open, so legacy
   * reports load cleanly without a separate backfill migration
   * (Phase 3 work, obviated). Phase 4 (executor cleanup of the legacy
   * collision-check / implicit-measure synthesis) and Phase 5 (deleting
   * this flag, the legacy editor branch, and the BE pivot* columns)
   * remain follow-ups.
   *
   * Known scope gap, default-on: `rowDerivedColumns` is not surfaced in
   * the RCV editor. Existing values persist through saves but cannot be
   * edited under the new editor; a user who needs to edit them must
   * fall back temporarily via the URL override above. Will be
   * re-surfaced as part of Phase 5 once the legacy editor branch is
   * removed.
   */
  "reporting.rcv.dropzoneEditor": true,
} as const;

export type FeatureFlagKey = keyof typeof FEATURE_FLAGS;

// Exported so bootstrap.ts (and tests) reuse the same prefixes —
// single source of truth. Both happen to be "ff." today; if they
// ever diverge (e.g. namespace one for org-scoped flags), every
// reader uses the right constant automatically.
export const STORAGE_PREFIX = "ff.";
export const URL_PREFIX = "ff.";

function readUrlOverride(key: FeatureFlagKey): boolean | null {
  if (typeof window === "undefined") return null;
  const params = new URLSearchParams(window.location.search);
  const v = params.get(`${URL_PREFIX}${key}`);
  if (v === null) return null;
  // Persist into localStorage so an in-app nav doesn't lose the toggle.
  try {
    window.localStorage.setItem(`${STORAGE_PREFIX}${key}`, v);
  } catch {
    /* private mode / quota — ignore */
  }
  return v === "1" || v === "true";
}

function readLocalOverride(key: FeatureFlagKey): boolean | null {
  if (typeof window === "undefined") return null;
  try {
    const v = window.localStorage.getItem(`${STORAGE_PREFIX}${key}`);
    if (v === null) return null;
    return v === "1" || v === "true";
  } catch {
    return null;
  }
}

export function isFeatureEnabled(key: FeatureFlagKey): boolean {
  return (
    readUrlOverride(key) ??
    readLocalOverride(key) ??
    FEATURE_FLAGS[key]
  );
}
